/**
 * @fileoverview User directory filters hook.
 * @description The User Management table's filter controls (search, Role, Reporting Manager,
 * State, Status), the debounced GET /users filters built from them (every "All" left out), whether
 * any is set, and how to clear them all.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'

import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'

import type { DirectoryStatus, ListUsersFilters } from '@/features/users/types/users.types'
import type { ChangeEvent } from 'react'

/** The "All" choice of every select filter. */
export const ALL_FILTER = ''

/** The table's filter controls. */
export interface TableFilters {
  search: string
  roleId: string
  managerId: string
  stateId: string
  status: DirectoryStatus | ''
}

/**
 * @description Builds GET /users' filters from the table's controls, leaving out every "All".
 * @param {TableFilters} filters - The controls' values (the search already debounced).
 * @returns {ListUsersFilters} The filters.
 */
export function toFilters({
  search,
  roleId,
  managerId,
  stateId,
  status,
}: TableFilters): ListUsersFilters {
  return {
    ...(search && { search }),
    ...(roleId && { roleId }),
    ...(managerId && { reportingManagerUserId: managerId }),
    ...(stateId && { stateId }),
    ...(status && { directoryStatus: status }),
  }
}

/**
 * @description Keeps the table's filter controls and the query filters built from them.
 * @returns {object} The control values, the debounced query filters, whether any control is set, a
 * change handler per control, and a way to clear them all.
 */
export function useUserDirectoryFilters() {
  const [query, setQuery] = useState('')
  const [roleId, setRoleId] = useState(ALL_FILTER)
  const [managerId, setManagerId] = useState(ALL_FILTER)
  const [stateId, setStateId] = useState(ALL_FILTER)
  const [status, setStatus] = useState<DirectoryStatus | ''>(ALL_FILTER)
  const search = useDebouncedValue(query.trim())

  /**
   * @description Resets every control back to its default.
   */
  const clear = () => {
    setQuery('')
    setRoleId(ALL_FILTER)
    setManagerId(ALL_FILTER)
    setStateId(ALL_FILTER)
    setStatus(ALL_FILTER)
  }

  /**
   * @description Applies the Role filter.
   * @param {ChangeEvent<HTMLSelectElement>} event - The select's change.
   */
  const changeRole = (event: ChangeEvent<HTMLSelectElement>) => {
    setRoleId(event.target.value)
  }

  /**
   * @description Applies the Reporting Manager filter.
   * @param {ChangeEvent<HTMLSelectElement>} event - The select's change.
   */
  const changeManager = (event: ChangeEvent<HTMLSelectElement>) => {
    setManagerId(event.target.value)
  }

  /**
   * @description Applies the State filter.
   * @param {ChangeEvent<HTMLSelectElement>} event - The select's change.
   */
  const changeState = (event: ChangeEvent<HTMLSelectElement>) => {
    setStateId(event.target.value)
  }

  /**
   * @description Applies the Status filter.
   * @param {ChangeEvent<HTMLSelectElement>} event - The select's change.
   */
  const changeStatus = (event: ChangeEvent<HTMLSelectElement>) => {
    setStatus(event.target.value as DirectoryStatus | '')
  }

  return {
    values: { query, roleId, managerId, stateId, status },
    queryFilters: toFilters({ search, roleId, managerId, stateId, status }),
    anyFilter:
      query.trim() !== '' ||
      roleId !== ALL_FILTER ||
      managerId !== ALL_FILTER ||
      stateId !== ALL_FILTER ||
      status !== ALL_FILTER,
    setQuery,
    changeRole,
    changeManager,
    changeState,
    changeStatus,
    clear,
  }
}
